import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Input from '@mui/material/Input';
import { ArrowLeft, Calendar, Users, Star, CheckCircle2, Plus, Trash2, Upload, X } from 'lucide-react';
import { updateRetreatStatus, getRetreatById, getRetreatListings } from '../../Services/Retreats/RetreatApis';
import { categoryActiveApi, subCategoryActiveApiWithoutPagination } from '../../Services/CategoryManagement/CategoryManagementApi';
import ConfirmationModal from '../../Components/Modals/ConfirmationModal';
import { getStatusColor } from './RetreatDetailsUtils';
import { generateSlug, normalizeSlugInput, cleanSlugFinal } from '../../utils.js/slugUtils';

// Import Tab Components
import RetreatDetailsTab from './Tabs/RetreatDetailsTab';
import LocationTab from './Tabs/LocationTab';
import SchedulePricingTab from './Tabs/SchedulePricingTab';
import AccommodationsTab from './Tabs/AccommodationsTab';
import MediaTab from './Tabs/MediaTab';
import FacilitatorsTab from './Tabs/FacilitatorsTab';
import FoodProgramTab from './Tabs/FoodProgramTab';
import AdditionalInfoTab from './Tabs/AdditionalInfoTab';
import TravelHelpTab from './Tabs/TravelHelpTab';


const RetreatDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: paramId } = useParams();
    const targetRetreatId = paramId || location.state?.retreat?.id || location.state?.id;

    const [retreat, setRetreat] = useState(null);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState(() => {
        const savedTab = sessionStorage.getItem('activeRetreatTab');
        return savedTab !== null ? parseInt(savedTab) : 0;
    });

    useEffect(() => {
        sessionStorage.setItem('activeRetreatTab', activeTab);
    }, [activeTab]);


    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [statusUpdateMessage, setStatusUpdateMessage] = useState(null);

    const [selectedCategory, setSelectedCategory] = useState([]);
    const [selectedSubCategory, setSelectedSubCategory] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedSlug, setSelectedSlug] = useState('');
    const [slugError, setSlugError] = useState('');
    const [selectedIsFeatured, setSelectedIsFeatured] = useState(false);
    const [selectedIsFeaturedHome, setSelectedIsFeaturedHome] = useState(false);
    const [selectedRetreatInvoiceType, setSelectedRetreatInvoiceType] = useState('regular');
    const [selectedCoordinationCharges, setSelectedCoordinationCharges] = useState('');
    const [selectedMetaTitle, setSelectedMetaTitle] = useState('');
    const [selectedMetaDescription, setSelectedMetaDescription] = useState('');
    const [selectedHeading, setSelectedHeading] = useState('');
    const [selectedMetaOutlet, setSelectedMetaOutlet] = useState([]);
    const [newMetaOutletInput, setNewMetaOutletInput] = useState('');
    const [selectedCanonicalUrl, setSelectedCanonicalUrl] = useState('');
    const [selectedIsIndex, setSelectedIsIndex] = useState(true);
    const [selectedImageAltText, setSelectedImageAltText] = useState('');
    const [ogImage, setOgImage] = useState(null);
    const [ogImagePreview, setOgImagePreview] = useState(null);
    const [ogImageError, setOgImageError] = useState('');
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        categories: false,
        subCategories: false
    });


    const fetchCategories = async () => {
        try {
            const response = await categoryActiveApi();
            if (response && response.success) {
                setCategories(response.data);
            }
        } catch (err) {
            console.error("Error fetching categories:", err);
        }
    };

    const fetchSubCategories = useCallback(async (catIds) => {
        try {
            if (!catIds || catIds.length === 0) {
                setSubCategories([]);
                setSelectedSubCategory([]);
                return;
            }

            const allSubCategories = await Promise.all(
                catIds.map(id => subCategoryActiveApiWithoutPagination(id))
            );

            const mergedSubCategories = allSubCategories
                .filter(res => res && res.success)
                .flatMap(res => res.data);

            // Remove duplicates if any
            const uniqueSubCategories = Array.from(new Map(mergedSubCategories.map(item => [item.id, item])).values());
            setSubCategories(uniqueSubCategories);

            // Filter out any selected subcategories that are no longer in the updated uniqueSubCategories list
            const uniqueSubIds = uniqueSubCategories.map(sub => Number(sub.id));
            setSelectedSubCategory(prev => prev.filter(id => uniqueSubIds.includes(Number(id))));
        } catch (err) {
            console.error("Error fetching sub-categories:", err);
        }
    }, []);

    // Meta Outlet Handlers
    const handleAddMetaOutletItem = () => {
        const trimmed = newMetaOutletInput.trim();
        if (!trimmed) return;
        setSelectedMetaOutlet(prev => [...prev, trimmed]);
        setNewMetaOutletInput('');
    };

    const handleRemoveMetaOutletItem = (index) => {
        setSelectedMetaOutlet(prev => prev.filter((_, idx) => idx !== index));
    };

    // OG Image Handlers
    const handleOgImageChange = (e) => {
        const file = e.target.files && e.target.files[0];
        const inputElement = e.target;
        if (file) {
            if (!file.type.startsWith('image/')) {
                setOgImageError('Only image files are allowed.');
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            if (file.size > 2 * 1024 * 1024) {
                setOgImageError('Image size must be less than 2MB.');
                setOgImage(null);
                setOgImagePreview(null);
                inputElement.value = "";
                return;
            }
            setOgImage(file);
            setOgImageError('');
            const reader = new FileReader();
            reader.onloadend = () => {
                setOgImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const handleRemoveOgImage = () => {
        setOgImage(null);
        setOgImagePreview(null);
        setOgImageError('');
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    // Extract category IDs from the retreat object returned by the list API
    // The list API uses `categoryIds` (array of numbers, plural)
    const extractCategoryIds = (r) => {
        if (!r) return [];
        if (r.categoryIds && Array.isArray(r.categoryIds)) return r.categoryIds.map(Number).filter(n => !isNaN(n));
        if (r.categoryId) {
            if (Array.isArray(r.categoryId)) return r.categoryId.map(id => typeof id === 'object' ? Number(id.id) : Number(id)).filter(n => !isNaN(n));
            if (typeof r.categoryId === 'string') return r.categoryId.split(',').map(id => Number(id.trim())).filter(n => !isNaN(n));
            return [Number(r.categoryId)].filter(n => !isNaN(n));
        }
        if (r.categories && Array.isArray(r.categories)) return r.categories.map(cat => Number(cat.id)).filter(n => !isNaN(n));
        return [];
    };

    // Extract subcategory IDs — list API uses `subCategoryIds` (plural)
    const extractSubCategoryIds = (r) => {
        if (!r) return [];
        if (r.subCategoryIds && Array.isArray(r.subCategoryIds)) return r.subCategoryIds.map(Number).filter(n => !isNaN(n));
        if (r.subCategoryId) {
            if (Array.isArray(r.subCategoryId)) return r.subCategoryId.map(id => typeof id === 'object' ? Number(id.id) : Number(id)).filter(n => !isNaN(n));
            if (typeof r.subCategoryId === 'string') return r.subCategoryId.split(',').map(id => Number(id.trim())).filter(n => !isNaN(n));
            return [Number(r.subCategoryId)].filter(n => !isNaN(n));
        }
        if (r.subCategories && Array.isArray(r.subCategories)) return r.subCategories.map(sub => Number(sub.id)).filter(n => !isNaN(n));
        return [];
    };

    const applyDetailsData = useCallback((details) => {
        if (!details) return;
        if (details.metaTitle !== undefined && details.metaTitle !== null) {
            setSelectedMetaTitle(details.metaTitle);
        }
        if (details.metaDescription !== undefined && details.metaDescription !== null) {
            setSelectedMetaDescription(details.metaDescription);
        }
        if (details.heading !== undefined && details.heading !== null) {
            setSelectedHeading(details.heading);
        }
        if (details.metaOutlet !== undefined && details.metaOutlet !== null) {
            setSelectedMetaOutlet(Array.isArray(details.metaOutlet) ? details.metaOutlet : []);
        }
        if (details.canonicalUrl !== undefined && details.canonicalUrl !== null) {
            setSelectedCanonicalUrl(details.canonicalUrl);
        }
        if (details.isIndex !== undefined && details.isIndex !== null) {
            setSelectedIsIndex(details.isIndex === "true" || details.isIndex === true);
        }
        if (details.imageAltText !== undefined && details.imageAltText !== null) {
            setSelectedImageAltText(details.imageAltText);
        }
        if (details.ogImage !== undefined && details.ogImage !== null) {
            setOgImagePreview(details.ogImage);
            setOgImage(null);
        }
        if (details.retreatInvoiceType) {
            setSelectedRetreatInvoiceType(details.retreatInvoiceType);
        }
        if (details.coordinationCharges !== undefined && details.coordinationCharges !== null) {
            setSelectedCoordinationCharges(String(details.coordinationCharges));
        }
        setRetreat((prev) => {
            if (!prev) return details;
            return {
                ...prev,
                ...details,
                metaTitle: details.metaTitle !== undefined && details.metaTitle !== null ? details.metaTitle : (prev.metaTitle || ''),
                metaDescription: details.metaDescription !== undefined && details.metaDescription !== null ? details.metaDescription : (prev.metaDescription || ''),
                heading: details.heading !== undefined && details.heading !== null ? details.heading : (prev.heading || ''),
                metaOutlet: details.metaOutlet !== undefined && details.metaOutlet !== null ? (Array.isArray(details.metaOutlet) ? details.metaOutlet : []) : (prev.metaOutlet || []),
                canonicalUrl: details.canonicalUrl !== undefined && details.canonicalUrl !== null ? details.canonicalUrl : (prev.canonicalUrl || ''),
                isIndex: details.isIndex !== undefined && details.isIndex !== null ? (details.isIndex === "true" || details.isIndex === true) : (prev.isIndex !== undefined ? prev.isIndex : true),
                imageAltText: details.imageAltText !== undefined && details.imageAltText !== null ? details.imageAltText : (prev.imageAltText || ''),
                ogImage: details.ogImage !== undefined && details.ogImage !== null ? details.ogImage : (prev.ogImage || null),
                retreatInvoiceType: details.retreatInvoiceType || prev.retreatInvoiceType || 'regular',
                coordinationCharges: details.coordinationCharges !== undefined && details.coordinationCharges !== null ? details.coordinationCharges : prev.coordinationCharges
            };
        });
    }, []);

    const populateRetreatData = useCallback((r) => {
        if (!r) return;
        const activeStatus = (r.status || '').toLowerCase();
        const activeSlug = cleanSlugFinal(r.slug || r.center?.slug || (r.title ? generateSlug(r.title) : ''));
        const activeIsFeatured = !!(r.isFeatured || r.featured);
        const activeIsFeaturedHome = !!(r.isFeaturedHome || r.featuredHome);
        const activeRetreatInvoiceType = r.retreatInvoiceType || 'regular';
        const activeCoordinationCharges = r.coordinationCharges !== undefined && r.coordinationCharges !== null ? String(r.coordinationCharges) : '';
        const activeMetaTitle = r.metaTitle || '';
        const activeMetaDescription = r.metaDescription || '';
        const activeHeading = r.heading || '';
        const activeMetaOutlet = Array.isArray(r.metaOutlet) ? r.metaOutlet : [];
        const activeCanonicalUrl = r.canonicalUrl || '';
        const activeIsIndex = r.isIndex !== undefined ? (r.isIndex === "true" || r.isIndex === true) : true;
        const activeImageAltText = r.imageAltText || '';
        const activeOgImage = r.ogImage || null;

        setRetreat((prev) => ({
            ...(prev || {}),
            ...r,
            slug: activeSlug,
            status: activeStatus,
            isFeatured: activeIsFeatured,
            isFeaturedHome: activeIsFeaturedHome,
            retreatInvoiceType: activeRetreatInvoiceType,
            coordinationCharges: activeCoordinationCharges !== '' ? Number(activeCoordinationCharges) : null,
            metaTitle: activeMetaTitle,
            metaDescription: activeMetaDescription,
            heading: activeHeading,
            metaOutlet: activeMetaOutlet,
            canonicalUrl: activeCanonicalUrl,
            isIndex: activeIsIndex,
            imageAltText: activeImageAltText,
            ogImage: activeOgImage
        }));
        setSelectedStatus(activeStatus);
        setSelectedSlug(activeSlug);
        setSlugError('');
        setSelectedIsFeatured(activeIsFeatured);
        setSelectedIsFeaturedHome(activeIsFeaturedHome);
        setSelectedRetreatInvoiceType(activeRetreatInvoiceType);
        setSelectedCoordinationCharges(activeCoordinationCharges);
        if (activeMetaTitle) setSelectedMetaTitle(activeMetaTitle);
        if (activeMetaDescription) setSelectedMetaDescription(activeMetaDescription);
        setSelectedHeading(activeHeading);
        setSelectedMetaOutlet(activeMetaOutlet);
        setSelectedCanonicalUrl(activeCanonicalUrl);
        setSelectedIsIndex(activeIsIndex);
        setSelectedImageAltText(activeImageAltText);
        setOgImagePreview(activeOgImage);
        setOgImage(null);
        setOgImageError('');

        const categoryIds = extractCategoryIds(r);
        const subCategoryIds = extractSubCategoryIds(r);

        setSelectedCategory(categoryIds);
        if (categoryIds.length > 0) {
            fetchSubCategories(categoryIds).then(() => {
                if (subCategoryIds.length > 0) setSelectedSubCategory(subCategoryIds);
            });
        } else {
            setSelectedSubCategory(subCategoryIds);
        }
    }, [fetchSubCategories]);

    useEffect(() => {
        const retreatId = targetRetreatId;

        if (location.state?.retreat) {
            populateRetreatData(location.state.retreat);
            setError(null);
        } else if (retreatId) {
            // Fetch both listing data and details data in parallel
            (async () => {
                try {
                    // Call both APIs in parallel since they don't depend on each other
                    const [listingRes, detailsRes] = await Promise.allSettled([
                        getRetreatListings(1, 10, '', '', '', '', retreatId),
                        getRetreatById(retreatId)
                    ]);

                    // Process listing API response
                    if (listingRes.status === 'fulfilled' && listingRes.value) {
                        const listingData = listingRes.value;
                        if (listingData.success && listingData.data && listingData.data.length > 0) {
                            // Find the current retreat in the listing response by ID to avoid using the wrong retreat
                            const currentRetreatInListing = listingData.data.find(item =>
                                item.id === Number(retreatId) ||
                                String(item.id) === String(retreatId)
                            );
                            if (currentRetreatInListing) {
                                populateRetreatData(currentRetreatInListing);
                                setError(null);
                            } else {
                                // If the specific retreat isn't found in listing, use the first item as fallback
                                populateRetreatData(listingData.data[0]);
                                setError(null);
                            }
                        }
                    }

                    // Process details API response
                    if (detailsRes.status === 'fulfilled' && detailsRes.value) {
                        const detailsData = detailsRes.value;
                        if (detailsData.success && detailsData.data) {
                            applyDetailsData(detailsData.data);
                        }
                    }

                    // If both failed, show error
                    if (listingRes.status === 'rejected' && detailsRes.status === 'rejected') {
                        setError("No retreat data found. Please go back and try again.");
                    }
                } catch (e) {
                    console.error("Error fetching retreat data:", e);
                    setError("Failed to load retreat data.");
                }
            })();
        } else {
            setError("No retreat data found. Please go back and try again.");
        }
    }, [targetRetreatId, location.state, populateRetreatData, applyDetailsData]);


    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleGoBack = () => {
        navigate('/retreat');
    };

    const handleSlugChange = (e) => {
        const val = normalizeSlugInput(e.target.value);
        setSelectedSlug(val);
        if (slugError) {
            setSlugError('');
        }
    };

    const handleSlugBlur = () => {
        const cleaned = cleanSlugFinal(selectedSlug);
        setSelectedSlug(cleaned);
        if (!cleaned) {
            setSlugError('Slug is required');
        } else {
            setSlugError('');
        }
    };

    const handleStatusUpdate = async (newStatus) => {
        if (!retreat || !retreat.id || updatingStatus) return;

        const statusToUpdate = selectedStatus ? selectedStatus.toLowerCase() : '';
        const currentStatus = (retreat.status || '').toLowerCase();

        // Calculate if categories, subcategories, slug or featured have changed
        const categoryIdsToUpdate = selectedCategory.map(Number).filter(n => !isNaN(n));
        const subCategoryIdsToUpdate = selectedSubCategory.map(Number).filter(n => !isNaN(n));
        const currentCategoryIds = extractCategoryIds(retreat);
        const currentSubCategoryIds = extractSubCategoryIds(retreat);

        const currentCleanSlug = cleanSlugFinal(retreat.slug || retreat.center?.slug || '');
        const selectedCleanSlug = cleanSlugFinal(selectedSlug);
        const slugChanged = selectedCleanSlug !== currentCleanSlug;

        const categoriesChanged = JSON.stringify(categoryIdsToUpdate.sort()) !== JSON.stringify(currentCategoryIds.sort());
        const subCategoriesChanged = JSON.stringify(subCategoryIdsToUpdate.sort()) !== JSON.stringify(currentSubCategoryIds.sort());
        const featuredChanged = selectedIsFeatured !== !!(retreat.isFeatured || retreat.featured);
        const featuredHomeChanged = selectedIsFeaturedHome !== !!(retreat.isFeaturedHome || retreat.featuredHome);
        const invoiceTypeChanged = (selectedRetreatInvoiceType || 'regular') !== (retreat.retreatInvoiceType || 'regular');
        const currentCoordinationChargesStr = retreat.coordinationCharges !== undefined && retreat.coordinationCharges !== null ? String(retreat.coordinationCharges) : '';
        const coordinationChargesChanged = String(selectedCoordinationCharges ?? '').trim() !== currentCoordinationChargesStr.trim();
        const metaTitleChanged = selectedMetaTitle !== (retreat.metaTitle || '');
        const metaDescriptionChanged = selectedMetaDescription !== (retreat.metaDescription || '');
        const headingChanged = selectedHeading !== (retreat.heading || '');
        const metaOutletChanged = JSON.stringify(selectedMetaOutlet) !== JSON.stringify(Array.isArray(retreat.metaOutlet) ? retreat.metaOutlet : []);
        const canonicalUrlChanged = selectedCanonicalUrl !== (retreat.canonicalUrl || '');
        const isIndexChanged = selectedIsIndex !== (retreat.isIndex !== undefined ? (retreat.isIndex === "true" || retreat.isIndex === true) : true);
        const imageAltTextChanged = selectedImageAltText !== (retreat.imageAltText || '');
        const ogImageChanged = Boolean(ogImage) || (retreat.ogImage && !ogImagePreview);

        if (statusToUpdate === currentStatus && !slugChanged && !categoriesChanged && !subCategoriesChanged && !featuredChanged && !featuredHomeChanged && !invoiceTypeChanged && !coordinationChargesChanged && !metaTitleChanged && !metaDescriptionChanged && !headingChanged && !metaOutletChanged && !canonicalUrlChanged && !isIndexChanged && !imageAltTextChanged && !ogImageChanged) {
            setStatusUpdateMessage({
                type: 'info',
                text: 'This status is already assigned and no other details have changed.'
            });
            return;
        }

        if (!selectedCleanSlug) {
            setSlugError('Slug is required');
            return;
        }

        // Perform field-level validation
        const errors = {
            categories: selectedCategory.length === 0,
            subCategories: selectedSubCategory.length === 0
        };

        setValidationErrors(errors);

        if (errors.categories || errors.subCategories) {
            return;
        }

        // Open confirmation modal
        setIsConfirmModalOpen(true);
    };

    const confirmStatusUpdate = async () => {
        if (updatingStatus) return;

        try {
            setUpdatingStatus(true);
            setStatusUpdateMessage(null);

            const statusToUpdate = selectedStatus ? selectedStatus.toLowerCase() : '';
            const categoryIdsToUpdate = selectedCategory.map(Number).filter(n => !isNaN(n));
            const subCategoryIdsToUpdate = selectedSubCategory.map(Number).filter(n => !isNaN(n));
            const finalSlug = cleanSlugFinal(selectedSlug);

            const response = await updateRetreatStatus(
                retreat.id,
                statusToUpdate,
                categoryIdsToUpdate,
                subCategoryIdsToUpdate,
                selectedIsFeatured,
                selectedIsFeaturedHome,
                selectedRetreatInvoiceType,
                selectedCoordinationCharges,
                finalSlug,
                selectedMetaTitle,
                selectedMetaDescription,
                selectedHeading,
                selectedMetaOutlet,
                selectedCanonicalUrl,
                selectedIsIndex,
                selectedImageAltText,
                ogImage
            );

            if (response && (response.success || response.status === 'success' || response.id || response.data)) {
                const responseData = response.data || response;
                const returnedStatus = responseData?.status || response?.status || responseData?.retreat?.status;
                const statusToUse = returnedStatus ? returnedStatus.toLowerCase() : statusToUpdate;
                const returnedSlug = responseData?.slug || responseData?.retreat?.slug || finalSlug;
                const returnedIsFeatured = responseData?.isFeatured !== undefined ? !!responseData.isFeatured : selectedIsFeatured;
                const returnedIsFeaturedHome = responseData?.isFeaturedHome !== undefined ? !!responseData.isFeaturedHome : selectedIsFeaturedHome;
                const returnedRetreatInvoiceType = responseData?.retreatInvoiceType || responseData?.retreat?.retreatInvoiceType || selectedRetreatInvoiceType;
                const returnedCoordinationCharges = responseData?.coordinationCharges !== undefined
                    ? responseData.coordinationCharges
                    : (responseData?.retreat?.coordinationCharges !== undefined
                        ? responseData.retreat.coordinationCharges
                        : (selectedCoordinationCharges !== '' ? Number(selectedCoordinationCharges) : null));
                const returnedMetaTitle = responseData?.metaTitle !== undefined ? responseData.metaTitle : selectedMetaTitle;
                const returnedMetaDescription = responseData?.metaDescription !== undefined ? responseData.metaDescription : selectedMetaDescription;
                const returnedHeading = responseData?.heading !== undefined ? responseData.heading : selectedHeading;
                const returnedMetaOutlet = responseData?.metaOutlet !== undefined ? (Array.isArray(responseData.metaOutlet) ? responseData.metaOutlet : []) : selectedMetaOutlet;
                const returnedCanonicalUrl = responseData?.canonicalUrl !== undefined ? responseData.canonicalUrl : selectedCanonicalUrl;
                const returnedIsIndex = responseData?.isIndex !== undefined ? (responseData.isIndex === "true" || responseData.isIndex === true) : selectedIsIndex;
                const returnedImageAltText = responseData?.imageAltText !== undefined ? responseData.imageAltText : selectedImageAltText;
                const returnedOgImage = responseData?.ogImage !== undefined ? responseData.ogImage : (ogImage ? ogImagePreview : (!ogImagePreview ? null : retreat.ogImage));

                // Update local retreat state and persist to history.state so reload retains it
                setRetreat(prev => {
                    const updated = {
                        ...prev,
                        slug: returnedSlug,
                        status: statusToUse,
                        categoryId: categoryIdsToUpdate,
                        subCategoryId: subCategoryIdsToUpdate,
                        isFeatured: returnedIsFeatured,
                        isFeaturedHome: returnedIsFeaturedHome,
                        retreatInvoiceType: returnedRetreatInvoiceType,
                        coordinationCharges: returnedCoordinationCharges,
                        metaTitle: returnedMetaTitle,
                        metaDescription: returnedMetaDescription,
                        heading: returnedHeading,
                        metaOutlet: returnedMetaOutlet,
                        canonicalUrl: returnedCanonicalUrl,
                        isIndex: returnedIsIndex,
                        imageAltText: returnedImageAltText,
                        ogImage: returnedOgImage
                    };

                    // Persist to history.state so browser back/forward and reload gets updated state
                    if (window.history.state && window.history.state.usr) {
                        window.history.replaceState({
                            ...window.history.state,
                            usr: {
                                ...window.history.state.usr,
                                retreat: updated
                            }
                        }, '');
                    }

                    return updated;
                });

                // Sync form selections with confirmed response
                setSelectedSlug(returnedSlug);
                setSelectedStatus(statusToUse);
                setSelectedIsFeatured(returnedIsFeatured);
                setSelectedIsFeaturedHome(returnedIsFeaturedHome);
                setSelectedRetreatInvoiceType(returnedRetreatInvoiceType);
                setSelectedCoordinationCharges(returnedCoordinationCharges !== null && returnedCoordinationCharges !== undefined ? String(returnedCoordinationCharges) : '');
                setSelectedMetaTitle(returnedMetaTitle);
                setSelectedMetaDescription(returnedMetaDescription);
                setSelectedHeading(returnedHeading);
                setSelectedMetaOutlet(returnedMetaOutlet);
                setSelectedCanonicalUrl(returnedCanonicalUrl);
                setSelectedIsIndex(returnedIsIndex);
                setSelectedImageAltText(returnedImageAltText);
                setOgImagePreview(returnedOgImage);
                setOgImage(null);
                setOgImageError('');

                setStatusUpdateMessage({
                    type: 'success',
                    text: response.message || `Status and details updated successfully!`
                });

                // Refetch both listing data and details data after update
                try {
                    const [listingRes, detailsRes] = await Promise.allSettled([
                        getRetreatListings(1, 10, '', '', '', '', retreat.id),
                        getRetreatById(retreat.id)
                    ]);

                    // Process listing API response
                    if (listingRes.status === 'fulfilled' && listingRes.value) {
                        const listingData = listingRes.value;
                        if (listingData.success && listingData.data && listingData.data.length > 0) {
                            // Find the current retreat in the listing response by ID to avoid using the wrong retreat
                            const currentRetreatInListing = listingData.data.find(item => 
                                item.id === retreat.id || 
                                item.id === Number(retreat.id) ||
                                String(item.id) === String(retreat.id)
                            );
                            if (currentRetreatInListing) {
                                populateRetreatData(currentRetreatInListing);
                            }
                        }
                    }

                    // Process details API response
                    if (detailsRes.status === 'fulfilled' && detailsRes.value) {
                        const detailsData = detailsRes.value;
                        if (detailsData.success && detailsData.data) {
                            applyDetailsData(detailsData.data);
                        }
                    }
                } catch (err) {
                    console.error("Error refetching retreat data after update:", err);
                }

                // Refetch categories to ensure SEO fields are current
                try {
                    await fetchCategories();
                } catch (err) {
                    console.error("Error refetching categories after update:", err);
                }

                // Refetch subcategories for selected categories
                try {
                    if (categoryIdsToUpdate.length > 0) {
                        await fetchSubCategories(categoryIdsToUpdate);
                    }
                } catch (err) {
                    console.error("Error refetching subcategories after update:", err);
                }

                // Clear message after 4 seconds
                setTimeout(() => setStatusUpdateMessage(null), 4000);
            } else {
                // Restore previous values in the dropdowns/state
                setSelectedSlug(cleanSlugFinal(retreat.slug || retreat.center?.slug || ''));
                setSelectedStatus((retreat.status || '').toLowerCase());
                setSelectedIsFeatured(!!(retreat.isFeatured || retreat.featured));
                setSelectedIsFeaturedHome(!!(retreat.isFeaturedHome || retreat.featuredHome));
                setSelectedRetreatInvoiceType(retreat.retreatInvoiceType || 'regular');
                setSelectedCoordinationCharges(retreat.coordinationCharges !== undefined && retreat.coordinationCharges !== null ? String(retreat.coordinationCharges) : '');
                setSelectedMetaTitle(retreat.metaTitle || '');
                setSelectedMetaDescription(retreat.metaDescription || '');
                setSelectedHeading(retreat.heading || '');
                setSelectedMetaOutlet(Array.isArray(retreat.metaOutlet) ? retreat.metaOutlet : []);
                setSelectedCanonicalUrl(retreat.canonicalUrl || '');
                setSelectedIsIndex(retreat.isIndex !== undefined ? (retreat.isIndex === "true" || retreat.isIndex === true) : true);
                setSelectedImageAltText(retreat.imageAltText || '');
                setOgImagePreview(retreat.ogImage || null);
                setOgImage(null);
                setOgImageError('');
                setSelectedCategory(extractCategoryIds(retreat));
                setSelectedSubCategory(extractSubCategoryIds(retreat));

                setStatusUpdateMessage({
                    type: 'error',
                    text: response?.message || 'Failed to update status'
                });
            }
        } catch (err) {
            console.error("Error updating retreat status:", err);
            const serverError = err.response?.data;
            let errorMessage = serverError?.message || serverError?.error;
            if (serverError?.details && Array.isArray(serverError.details)) {
                errorMessage = serverError.details.map(d => d.message).join(', ');
            } else if (serverError?.errors && Array.isArray(serverError.errors)) {
                errorMessage = serverError.errors.join(', ');
            }

            // Restore previous values in the dropdowns/state
            setSelectedSlug(cleanSlugFinal(retreat.slug || retreat.center?.slug || ''));
            setSelectedStatus((retreat.status || '').toLowerCase());
            setSelectedIsFeatured(!!(retreat.isFeatured || retreat.featured));
            setSelectedIsFeaturedHome(!!(retreat.isFeaturedHome || retreat.featuredHome));
            setSelectedRetreatInvoiceType(retreat.retreatInvoiceType || 'regular');
            setSelectedCoordinationCharges(retreat.coordinationCharges !== undefined && retreat.coordinationCharges !== null ? String(retreat.coordinationCharges) : '');
            setSelectedMetaTitle(retreat.metaTitle || '');
            setSelectedMetaDescription(retreat.metaDescription || '');
            setSelectedHeading(retreat.heading || '');
            setSelectedMetaOutlet(Array.isArray(retreat.metaOutlet) ? retreat.metaOutlet : []);
            setSelectedCanonicalUrl(retreat.canonicalUrl || '');
            setSelectedIsIndex(retreat.isIndex !== undefined ? (retreat.isIndex === "true" || retreat.isIndex === true) : true);
            setSelectedImageAltText(retreat.imageAltText || '');
            setOgImagePreview(retreat.ogImage || null);
            setOgImage(null);
            setOgImageError('');
            setSelectedCategory(extractCategoryIds(retreat));
            setSelectedSubCategory(extractSubCategoryIds(retreat));

            setStatusUpdateMessage({
                type: 'error',
                text: errorMessage || err.message || 'Error updating status. Please try again.'
            });
        } finally {
            setUpdatingStatus(false);
            setIsConfirmModalOpen(false);
        }
    };

    if (error) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
                <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                    variant="contained"
                    startIcon={<ArrowLeft size={18} />}
                    onClick={handleGoBack}
                    sx={{ bgcolor: '#FFBD65' }}
                >
                    Go Back
                </Button>
            </Box>
        );
    }

    if (!retreat) {
        return (
            <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#FBFBFB', minHeight: '100vh' }}>
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Typography sx={{ color: '#222', fontSize: { xs: '1.75rem', md: '1.8rem' }, }} >
                    Retreat Details
                </Typography>

                <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                    variant="contained" startIcon={<ArrowLeft size={16} />} onClick={handleGoBack}
                    sx={{ bgcolor: '#FFBD65', color: "#fff" }}
                >
                    Back to Retreats
                </Button>
            </Stack>

            {/* Main Content */}
            <Grid container spacing={3} alignItems="flex-start">
                {/* Basic Information */}
                <Grid size={{ xs: 12, md: 8 }}>
                    <Paper
                        sx={{ borderRadius: "8px", border: "1px solid #E8E8E8", p: 0, mb: 3 }}
                        elevation={0}
                    >
                        {/* Tabs */}
                        <Tabs
                            value={activeTab}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.875rem',
                                    minWidth: 'auto',
                                    px: 2,
                                    color: '#222',
                                },
                                '& .Mui-selected': {
                                    color: '#FFBD65',
                                    fontWeight: 600,
                                },
                                '& .MuiTabs-indicator': {
                                    backgroundColor: '#FFBD65',
                                },
                            }}
                        >
                            <Tab label="Retreat Details" />
                            <Tab label="Location Information" />
                            <Tab label="Schedule & Pricing" />
                            <Tab label="Accommodations" />
                            <Tab label="Media" />
                            <Tab label="Facilitators" />
                            <Tab label="Food & Program" />
                            <Tab label="Additional Info" />
                            <Tab label="Travel Help" />
                        </Tabs>

                        {/* Tab Content */}
                        <Box sx={{ p: 3 }}>
                            {/* Retreat Details Tab */}
                            {activeTab === 0 && (
                                <RetreatDetailsTab retreat={retreat} onDetailsLoaded={applyDetailsData} />
                            )}

                            {/* Location Information Tab */}
                            {activeTab === 1 && (
                                <LocationTab retreat={retreat} />
                            )}

                            {/* Schedule & Pricing Tab */}
                            {activeTab === 2 && (
                                <SchedulePricingTab retreat={retreat} />
                            )}

                            {/* Accommodations Tab */}
                            {activeTab === 3 && (
                                <AccommodationsTab retreat={retreat} />
                            )}

                            {/* Media Tab */}
                            {activeTab === 4 && (
                                <MediaTab retreat={retreat} />
                            )}

                            {/* Facilitators Tab */}
                            {activeTab === 5 && (
                                <FacilitatorsTab retreat={retreat} />
                            )}

                            {/* Food & Program Tab */}
                            {activeTab === 6 && (
                                <FoodProgramTab retreat={retreat} />
                            )}

                            {/* Additional Info Tab */}
                            {activeTab === 7 && (
                                <AdditionalInfoTab retreat={retreat} />
                            )}

                            {/* Travel Help Tab */}
                            {activeTab === 8 && (
                                <TravelHelpTab retreat={retreat} />
                            )}

                        </Box>
                    </Paper>
                </Grid>

                {/* Sidebar Information */}
                <Grid size={{ xs: 12, md: 4 }} sx={{ position: { md: 'sticky' }, top: { md: 24 }, zIndex: 1, height: 'fit-content' }}>
                    {/* Status Card */}
                    <Card sx={{ mb: 2, border: "1px solid #E8E8E8" }} elevation={0}>
                        <CardContent>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                Status & Details
                            </Typography>

                            <Stack spacing={2}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="body2" color="text.secondary">
                                        Status
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Chip
                                            label={retreat.status || 'N/A'}
                                            size="small"
                                            sx={getStatusColor(retreat.status)}
                                        />
                                        {updatingStatus && (
                                            <CircularProgress size={16} sx={{ color: '#FFBD65' }} />
                                        )}
                                    </Stack>
                                </Stack>

                                {/* Slug Input */}
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Slug"
                                    name="slug"
                                    value={selectedSlug}
                                    onChange={handleSlugChange}
                                    onBlur={handleSlugBlur}
                                    error={Boolean(slugError)}
                                    helperText={slugError || "URL slug for this retreat"}
                                    disabled={updatingStatus}
                                    placeholder="e.g. raddison-star-mading"
                                    sx={{
                                        mt: 1.5,
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            fontSize: '0.85rem',
                                            fontFamily: 'monospace',
                                            '& fieldset': { borderColor: '#E8E8E8' },
                                            '&:hover fieldset': { borderColor: '#FFBD65' },
                                            '&.Mui-focused fieldset': { borderColor: '#FFBD65' }
                                        }
                                    }}
                                />

                                {/* Category Selection */}
                                <Box sx={{ mt: 2 }}>
                                    <Autocomplete multiple size="small" options={categories || []}
                                        getOptionLabel={(option) => option.name}
                                        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                                        value={categories.filter(cat => selectedCategory.map(Number).includes(Number(cat.id)))}
                                        disabled={updatingStatus}
                                        onChange={(e, newValue) => {
                                            const ids = newValue.map(v => Number(v.id));
                                            setSelectedCategory(ids);
                                            setValidationErrors(prev => ({ ...prev, categories: ids.length === 0 }));
                                            if (ids.length > 0) {
                                                fetchSubCategories(ids);
                                            } else {
                                                setSubCategories([]);
                                                setSelectedSubCategory([]);
                                            }
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Categories"
                                                variant="outlined"
                                                sx={{ mb: 1.5 }}
                                                error={validationErrors.categories}
                                                helperText={validationErrors.categories ? "Required" : ""}
                                            />
                                        )}
                                        sx={{
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                        }}
                                    />

                                    <Autocomplete
                                        multiple
                                        size="small"
                                        disabled={updatingStatus || selectedCategory.length === 0}
                                        options={subCategories || []}
                                        getOptionLabel={(option) => option.name}
                                        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                                        value={subCategories.filter(sub => selectedSubCategory.map(Number).includes(Number(sub.id)))}
                                        onChange={(e, newValue) => {
                                            const ids = newValue.map(v => Number(v.id));
                                            setSelectedSubCategory(ids);
                                            setValidationErrors(prev => ({ ...prev, subCategories: ids.length === 0 }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Sub Categories"
                                                variant="outlined"
                                                sx={{ mb: 1.5 }}
                                                error={validationErrors.subCategories}
                                                helperText={validationErrors.subCategories ? "Required" : ""}
                                            />
                                        )}
                                        sx={{
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                        }}
                                    />
                                </Box>

                                {/* SEO Section */}
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #F1F5F9' }}>
                                    <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5 }}>
                                        SEO
                                    </Typography>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                        Configure SEO settings for this retreat.
                                    </Typography>

                                    {/* Meta Title */}
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Meta Title"
                                        placeholder="Enter meta title"
                                        disabled={updatingStatus}
                                        value={selectedMetaTitle}
                                        onChange={(e) => setSelectedMetaTitle(e.target.value)}
                                        sx={{
                                            mb: 1.5,
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                        }}
                                    />

                                    {/* Meta Description */}
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Meta Description"
                                        placeholder="Enter meta description"
                                        multiline
                                        rows={3}
                                        disabled={updatingStatus}
                                        value={selectedMetaDescription}
                                        onChange={(e) => setSelectedMetaDescription(e.target.value)}
                                        sx={{
                                            mb: 1.5,
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                        }}
                                    />

                                    {/* Heading */}
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Heading"
                                        placeholder="Enter heading"
                                        disabled={updatingStatus}
                                        value={selectedHeading}
                                        onChange={(e) => setSelectedHeading(e.target.value)}
                                        sx={{
                                            mb: 1.5,
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                        }}
                                    />

                                    {/* Meta Outlet */}
                                    <Typography sx={{ fontWeight: 600, color: '#1E293B', fontSize: '0.875rem', mb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CheckCircle2 size={16} color="#6E8964" /> Meta Outlet (H2  Outlet)
                                    </Typography>
                                    <Typography sx={{ color: '#64748B', fontSize: '0.75rem', mb: 2 }}>
                                        Add meta outlet bullet points for this retreat.
                                    </Typography>

                                    <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                                        <TextField
                                            placeholder="Add meta outlet item..."
                                            value={newMetaOutletInput}
                                            onChange={(e) => setNewMetaOutletInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleAddMetaOutletItem();
                                                }
                                            }}
                                            fullWidth
                                            size="small"
                                            disabled={updatingStatus}
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: '6px',
                                                    fontSize: '0.8rem',
                                                    bgcolor: '#FFFFFF',
                                                    '& fieldset': { borderColor: '#E2E8F0' },
                                                    '&:hover fieldset': { borderColor: '#CBD5E1' },
                                                    '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
                                                }
                                            }}
                                        />
                                        <Button
                                            variant="contained"
                                            startIcon={<Plus size={16} />}
                                            onClick={handleAddMetaOutletItem}
                                            disabled={updatingStatus}
                                            sx={{
                                                backgroundColor: '#1E293B',
                                                color: '#FFFFFF',
                                                textTransform: 'none',
                                                boxShadow: 'none',
                                                borderRadius: '6px',
                                                fontSize: '0.8rem',
                                                fontWeight: 500,
                                                px: 2.5,
                                                '&:hover': { backgroundColor: '#334155', boxShadow: 'none' }
                                            }}
                                        >
                                            Add
                                        </Button>
                                    </Stack>

                                    <Stack spacing={1}>
                                        {selectedMetaOutlet.map((item, idx) => (
                                            <Box
                                                key={idx}
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'center',
                                                    p: 1.25,
                                                    backgroundColor: '#F8FAFC',
                                                    border: '1px solid #E2E8F0',
                                                    borderRadius: '6px'
                                                }}
                                            >
                                                <Typography sx={{ color: '#334155', fontSize: '0.8rem', fontWeight: 500 }}>
                                                    • {item}
                                                </Typography>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleRemoveMetaOutletItem(idx)}
                                                    disabled={updatingStatus}
                                                    sx={{ color: '#94A3B8', p: 0.5, '&:hover': { color: '#EF4444', backgroundColor: '#FEE2E2' } }}
                                                >
                                                    <Trash2 size={15} />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Stack>

                                    {/* Canonical URL */}
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Canonical URL"
                                        placeholder="Enter canonical URL"
                                        disabled={updatingStatus}
                                        value={selectedCanonicalUrl}
                                        onChange={(e) => setSelectedCanonicalUrl(e.target.value)}
                                        sx={{
                                            mb: 1.5,
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                        }}
                                    />

                                    {/* Image Alt Text */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                            Image Alt Text
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Enter image alt text"
                                            disabled={updatingStatus}
                                            value={selectedImageAltText}
                                            onChange={(e) => setSelectedImageAltText(e.target.value)}
                                            sx={{
                                                '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                            }}
                                        />
                                    </Box>

                                    {/* OG Image */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <Typography sx={{ mb: 0.75, fontSize: '0.8rem', fontWeight: 500, color: "#334155" }}>
                                            OG Image
                                        </Typography>
                                        {!ogImagePreview ? (
                                            <Box
                                                component="label"
                                                sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    border: ogImageError ? "1px dashed #EF4444" : "1px dashed #CBD5E1",
                                                    borderRadius: "6px",
                                                    height: 140,
                                                    cursor: updatingStatus ? "not-allowed" : "pointer",
                                                    bgcolor: "#F8FAFC",
                                                    transition: "all 0.2s ease-in-out",
                                                    "&:hover": {
                                                        borderColor: ogImageError ? "#EF4444" : "#6E8964",
                                                        bgcolor: "#F1F5F9"
                                                    }
                                                }}
                                            >
                                                <Input
                                                    type="file"
                                                    inputProps={{ accept: "image/png, image/jpeg, image/jpg, image/webp, image/gif, image/svg+xml, image/*" }}
                                                    sx={{ display: "none" }}
                                                    disabled={updatingStatus}
                                                    onChange={handleOgImageChange}
                                                />
                                                <Upload size={22} color={ogImageError ? "#EF4444" : "#64748B"} style={{ marginBottom: 6 }} />
                                                <Typography sx={{ color: ogImageError ? "#EF4444" : "#475569", fontSize: '0.8rem', fontWeight: 500 }}>
                                                    Click to upload OG image
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: ogImageError ? "#EF4444" : "#94A3B8", fontSize: '0.7rem' }}>
                                                    PNG, JPG or WebP (Max 2MB)
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box sx={{ position: "relative", width: "100%", height: 140, bgcolor: "#F8FAFC", borderRadius: "6px", border: "1px solid #E2E8F0", display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                                <Box
                                                    component="img"
                                                    src={ogImagePreview}
                                                    alt="OG Image Preview"
                                                    sx={{
                                                        maxWidth: "100%",
                                                        maxHeight: "100%",
                                                        objectFit: "contain",
                                                    }}
                                                />
                                                <IconButton
                                                    onClick={handleRemoveOgImage}
                                                    disabled={updatingStatus}
                                                    sx={{
                                                        position: "absolute",
                                                        top: 6,
                                                        right: 6,
                                                        backgroundColor: "#FFFFFF",
                                                        border: "1px solid #E2E8F0",
                                                        color: "#EF4444",
                                                        p: 0.5,
                                                        boxShadow: 'none',
                                                        '&:hover': { bgcolor: '#FEE2E2' }
                                                    }}
                                                    size="small"
                                                >
                                                    <X size={14} />
                                                </IconButton>
                                            </Box>
                                        )}
                                        {ogImageError && (
                                            <Typography sx={{ color: '#EF4444', fontSize: '0.75rem', mt: 0.75, fontWeight: 400 }}>
                                                {ogImageError}
                                            </Typography>
                                        )}
                                    </Box>

                                    {/* Is Index */}
                                    <Box sx={{ mb: 1.5 }}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(selectedIsIndex)}
                                                    disabled={updatingStatus}
                                                    onChange={(e) => setSelectedIsIndex(e.target.checked)}
                                                    sx={{
                                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                                            color: '#6E8964',
                                                        },
                                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                                            backgroundColor: '#6E8964',
                                                        },
                                                    }}
                                                />
                                            }
                                            label={<Typography sx={{ fontWeight: 500, fontSize: '0.825rem', color: "#334155" }}>Is Index</Typography>}
                                        />
                                    </Box>
                                </Box>


                                {/* Status Selection */}
                                <Box sx={{ mt: 1 }}>
                                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                        <InputLabel id="isFeatured-label">Featured</InputLabel>
                                        <Select
                                            labelId="isFeatured-label"
                                            value={selectedIsFeatured ? 'yes' : 'no'}
                                            label="Featured"
                                            disabled={updatingStatus}
                                            onChange={(e) => setSelectedIsFeatured(e.target.value === 'yes')}
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            <MenuItem value="yes">Yes</MenuItem>
                                            <MenuItem value="no">No</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                        <InputLabel id="isFeaturedHome-label">Featured Home</InputLabel>
                                        <Select
                                            labelId="isFeaturedHome-label"
                                            value={selectedIsFeaturedHome ? 'yes' : 'no'}
                                            label="Featured Home"
                                            disabled={updatingStatus}
                                            onChange={(e) => setSelectedIsFeaturedHome(e.target.value === 'yes')}
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            <MenuItem value="yes">Yes</MenuItem>
                                            <MenuItem value="no">No</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                                        <InputLabel id="retreatInvoiceType-label">Retreat Invoice Type</InputLabel>
                                        <Select
                                            labelId="retreatInvoiceType-label"
                                            value={selectedRetreatInvoiceType}
                                            label="Retreat Invoice Type"
                                            disabled={updatingStatus}
                                            onChange={(e) => setSelectedRetreatInvoiceType(e.target.value)}
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            <MenuItem value="regular">Regular</MenuItem>
                                            <MenuItem value="ccc">CCC</MenuItem>
                                        </Select>
                                    </FormControl>

                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Coordination Charges"
                                        placeholder="Enter coordination charges"
                                        disabled={updatingStatus}
                                        value={selectedCoordinationCharges}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            // Allow only numbers and limit length to 10 digits
                                            if (val === '' || (/^\d+$/.test(val) && val.length <= 10)) {
                                                setSelectedCoordinationCharges(val);
                                            }
                                        }}
                                        inputProps={{
                                            maxLength: 10,
                                            inputMode: 'numeric',
                                            pattern: '[0-9]*'
                                        }}
                                        sx={{
                                            mb: 1.5,
                                            '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                                        }}
                                    />

                                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                        <InputLabel id="status-label">Update Status</InputLabel>
                                        <Select
                                            labelId="status-label"
                                            value={selectedStatus}
                                            label="Update Status"
                                            disabled={updatingStatus}
                                            onChange={(e) => setSelectedStatus(e.target.value)}
                                            sx={{ borderRadius: '8px' }}
                                        >
                                            {['draft', 'published', 'rejected'].map((status) => (
                                                <MenuItem key={status} value={status}>
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                        <Button disableElevation disableFocusRipple disableRipple disableTouchRipple
                                            variant="contained" onClick={() => handleStatusUpdate(selectedStatus)}
                                            disabled={updatingStatus}
                                            sx={{
                                                bgcolor: '#FFBD65', color: '#fff', borderRadius: '4px',
                                                py: 0.75, px: 3.8, boxShadow: 'none', textTransform: 'none',
                                            }}
                                        >
                                            {updatingStatus ? <CircularProgress size={20} color="inherit" /> : 'Update Retreat'}
                                        </Button>
                                    </Box>
                                </Box>

                                {/* Status Update Message */}
                                {statusUpdateMessage && (
                                    <Alert severity={statusUpdateMessage.type}
                                        sx={{ mt: 1, py: 0.5, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
                                    >
                                        {statusUpdateMessage.text}
                                    </Alert>
                                )}

                                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-around", gap: "10px" }}>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Average Rating
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={0.5}>
                                            {[1, 2, 3, 4, 5].map((star) => (
                                                <Star
                                                    key={star}
                                                    size={15}
                                                    color="#FFBD65"
                                                    fill={star <= Math.round(parseFloat(retreat.averageRating || 0)) ? '#FFBD65' : 'none'}
                                                />
                                            ))}
                                            <Typography variant="body2" sx={{ fontWeight: 600, ml: 0.5 }}>
                                                {Math.round(parseFloat(retreat.averageRating || 0))}
                                            </Typography>
                                        </Stack>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                            Total Reviews
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Users size={16} color="#666" />
                                            <Typography variant="body1">
                                                {retreat.totalReviews ?? 0}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </Box>
                                <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "space-around", gap: "10px" }}>


                                    <Box >
                                        <Typography variant="body2" color="text.secondary">
                                            Created Date
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Calendar size={16} color="#666" />
                                            <Typography variant="body2">
                                                {new Date(retreat.createdAt).toLocaleDateString()}
                                            </Typography>
                                        </Stack>
                                    </Box>

                                    <Box>
                                        <Typography variant="body2" color="text.secondary">
                                            Last Updated
                                        </Typography>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Calendar size={16} color="#666" />
                                            <Typography variant="body2">
                                                {new Date(retreat.updatedAt).toLocaleDateString()}
                                            </Typography>
                                        </Stack>
                                    </Box>
                                </Box>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Confirmation Modal */}
            <ConfirmationModal
                open={isConfirmModalOpen}
                onClose={() => setIsConfirmModalOpen(false)}
                onConfirm={confirmStatusUpdate}
                title="Update Retreat Status"
                description={`Are you sure you want to update this retreat to "${selectedStatus}"? This will save the selected categories, featured status, invoice type, coordination charges, and SEO fields as well.`}
                loading={updatingStatus}
            />


        </Box>
    );
};


export default RetreatDetails;
